import { Injectable, inject } from '@angular/core';
import { HttpService } from '@nha/api/shared';
import { CoreApiConfiguration } from '../core-api-configuration.class';
import { appSettings } from '@nha/config';

import { lastValueFrom, of, take, tap } from 'rxjs';
import { Setting } from '../types/setting.type';

@Injectable({
	providedIn: 'root'
})
export class SettingService extends HttpService {
	constructor() {
		const config = inject(CoreApiConfiguration);

		super(config, 'setting');
	}

	async loadFrontendSettings(): Promise<void> {
		const cachedSettings = localStorage.getItem(`settings`);
		if (cachedSettings) {
			await lastValueFrom(of(JSON.parse(cachedSettings))).then((settings) => {
				// use cached settings until settings are loaded from backend
				this.attachAppSettings(settings);
			});
			this.loadFromBackend();
		} else {
			await this.loadFromBackend();
		}
	}

	loadFromBackend(): Promise<void> {
		const cachedSettings = localStorage.getItem(`settings`);

		return lastValueFrom(
			this.getAll<Setting>().pipe(
				take(1),
				tap((settingsFromDB) => {
					// update cached settings
					cachedSettings !== JSON.stringify(settingsFromDB) && this.updateCachedSettings(settingsFromDB);
				})
			)
		).then((settings) => this.attachAppSettings(settings));
	}

	private updateCachedSettings(settings: Setting[]) {
		localStorage.setItem(`settings`, JSON.stringify(settings));
	}

	private attachAppSettings(settings: Setting[]) {
		const dbSettings: { [key: string]: string | number | boolean } = {};
		settings.forEach((setting) => (dbSettings[setting.settingKey] = this.getSettingValue(setting.settingValue)));
		appSettings.dbSettings = dbSettings;
	}

	private getSettingValue(value: number | string | boolean): number | string | boolean {
		if ((value as string).toLowerCase() === 'true') {
			return true;
		}

		if ((value as string).toLowerCase() === 'false') {
			return false;
		}

		const numValue: number = Number(value);
		if (isNaN(numValue)) {
			return value;
		}

		return numValue;
	}
}
